const PAYWALL_TRACE_STORAGE_KEY = "jungle_latest_paywall_trace";
const PAYWALL_TRACE_EXPIRATION_MS = 30 * 60 * 1000;

export type LatestPaywallTraceProperties = {
  paywallTraceId?: string;
  paywallSurface?: string;
  paywallReplaySampleRate?: number;
  paywallReplayStartedAt?: string;
  paywallReplayStartReason?: string;
  predictiveReplayStartedAt?: string;
  predictiveReplaySignal?: string;
  predictiveReplaySignalSource?: string;
  paywallVariant?: string;
  context?: string;
  reasonForUpsell?: string;
  subscriptionPlanToShow?: string;
  paywallOpenedAt?: string;
  paywallOpenedPathname?: string;
  paywallOpenedSearch?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  lastUpdatedAt?: string;
};

type StoredPaywallTrace = LatestPaywallTraceProperties & {
  storedAtMs: number;
};

function readStoredPaywallTrace(): StoredPaywallTrace | null {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;

    const rawStoredPaywallTrace = window.sessionStorage.getItem(PAYWALL_TRACE_STORAGE_KEY);
    if (!rawStoredPaywallTrace) return null;

    return JSON.parse(rawStoredPaywallTrace) as StoredPaywallTrace;
  } catch {
    return null;
  }
}

function isStoredPaywallTraceExpired(storedPaywallTrace: StoredPaywallTrace | null) {
  return !storedPaywallTrace?.storedAtMs || Date.now() - storedPaywallTrace.storedAtMs > PAYWALL_TRACE_EXPIRATION_MS;
}

export function saveLatestPaywallTraceProperties(paywallTraceProperties: LatestPaywallTraceProperties) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;

    const existingStoredPaywallTrace = readStoredPaywallTrace();
    const validExistingStoredPaywallTrace = isStoredPaywallTraceExpired(existingStoredPaywallTrace)
      ? null
      : existingStoredPaywallTrace;
    const { storedAtMs: existingStoredAtMs, ...existingPaywallTraceProperties } =
      validExistingStoredPaywallTrace || {};
    const isNewPaywallTrace =
      !validExistingStoredPaywallTrace?.paywallTraceId ||
      validExistingStoredPaywallTrace.paywallTraceId !== paywallTraceProperties.paywallTraceId;
    const storedPaywallTrace: StoredPaywallTrace = {
      ...existingPaywallTraceProperties,
      ...paywallTraceProperties,
      storedAtMs: isNewPaywallTrace || !existingStoredAtMs ? Date.now() : existingStoredAtMs,
      lastUpdatedAt: new Date().toISOString(),
    };

    window.sessionStorage.setItem(PAYWALL_TRACE_STORAGE_KEY, JSON.stringify(storedPaywallTrace));
  } catch {}
}

export function getLatestPaywallTraceProperties(): LatestPaywallTraceProperties {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return {};

    const storedPaywallTrace = readStoredPaywallTrace();
    if (!storedPaywallTrace) return {};

    if (isStoredPaywallTraceExpired(storedPaywallTrace)) {
      window.sessionStorage.removeItem(PAYWALL_TRACE_STORAGE_KEY);
      return {};
    }

    const { storedAtMs, ...latestPaywallTraceProperties } = storedPaywallTrace;
    return latestPaywallTraceProperties;
  } catch {
    return {};
  }
}
